import { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getBusinessLinesPaged,
  createBusinessLine,
  updateBusinessLine,
  deleteBusinessLine,
} from '../../../../../services/bussinesLineServices';
import BusinessCreateEditModal from './BusinessCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configBusinessI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const FILTER_VALUES = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo') return 'inactive';
  return 'active';
};

const normalizeFilterValue = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['all', 'todos'].includes(normalized)) return FILTER_VALUES.all;
  if (['active', 'activos', 'activo'].includes(normalized)) {
    return FILTER_VALUES.active;
  }
  if (['inactive', 'inactivos', 'inactivo'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return null;
};

const toBackendStatusFilter = (value) => {
  switch (value) {
    case FILTER_VALUES.active:
      return 'Activos';
    case FILTER_VALUES.inactive:
      return 'Inactivos';
    default:
      return 'Todos';
  }
};

export default function ConfigBusiness() {
  const { language } = useLanguage();
  const businessLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configBusinessI18n });
  const { showSnackbar } = useSnackbar();

  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Button, Input, Table, Badge, IconButton, Modal, Dropdown } =
    DesignSystemComponents ?? {};

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [statusFilterValue, setStatusFilterValue] = useState(FILTER_VALUES.active);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValue, setFormValue] = useState({
    id: null,
    name: '',
    status: 'active',
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [searchDebounced, setSearchDebounced] = useState('');
  const stateSearch = location.state?.search ?? '';
  const stateStatus = location.state?.status ?? '';

  useEffect(() => {
    configBusinessI18n.changeLanguage(businessLanguage);
  }, [businessLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_business_filter_all'), value: FILTER_VALUES.all },
      { label: t('config_business_filter_active'), value: FILTER_VALUES.active },
      {
        label: t('config_business_filter_inactive'),
        value: FILTER_VALUES.inactive,
      },
    ],
    [t]
  );

  const selectedStatusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[1];

  useEffect(() => {
    if (stateSearch) {
      setSearch(stateSearch);
      setPage(0);
    }
  }, [stateSearch]);

  useEffect(() => {
    if (!stateStatus) return;
    const normalized = normalizeFilterValue(stateStatus);
    if (normalized) {
      setStatusFilterValue(normalized);
      setPage(0);
    }
  }, [stateStatus]);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'status':
        return 'isEnabled';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'name';
  const backendSortDesc =
    sortDirection === null ? false : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const mapBusinessLineRow = (line) => ({
    id: line.id,
    name: line.name || '',
    status: line.isEnabled ? 'active' : 'inactive',
  });

  useEffect(() => {
    let mounted = true;

    const fetchBusinessLines = async () => {
      try {
        setLoading(true);

        const backendPage = page + 1;

        const response = await getBusinessLinesPaged(
          backendPage,
          pageSize,
          toBackendStatusFilter(statusFilterValue),
          searchDebounced || null,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mappedBusinessLines = response.businessLines.map(mapBusinessLineRow);

        setRows(mappedBusinessLines);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading business lines:', error);
        if (mounted) {
          showSnackbar(error.customMessage || t('config_business_error_load'), 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchBusinessLines();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    statusFilterValue,
    t,
  ]);

  const statusColor = (status) =>
    normalizeStatus(status) === 'active' ? 'success' : 'light';

  const openCreate = () => {
    setFormMode('create');
    setFormValue({ id: null, name: '', status: 'active' });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setFormValue({ ...row, status: normalizeStatus(row?.status) });
    setIsFormOpen(true);
  };

  const openDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const reloadBusinessLines = async () => {
    const backendPage = page + 1;
    const response = await getBusinessLinesPaged(
      backendPage,
      pageSize,
      toBackendStatusFilter(statusFilterValue),
      searchDebounced || null,
      backendSortKey,
      backendSortDesc
    );

    const mappedBusinessLines = response.businessLines.map(mapBusinessLineRow);

    setRows(mappedBusinessLines);
    setTotal(response.total);
  };

  const saveForm = async (updatedValue) => {
    const dataToSave = updatedValue || formValue;

    if (!dataToSave.name.trim()) return;

    try {
      if (formMode === 'create') {
        await createBusinessLine({
          name: dataToSave.name,
          isEnabled: normalizeStatus(dataToSave.status) === 'active',
        });

        showSnackbar(t('config_business_success_created'), 'success');
      } else {
        await updateBusinessLine(dataToSave.id, {
          name: dataToSave.name,
          isEnabled: normalizeStatus(dataToSave.status) === 'active',
        });

        showSnackbar(t('config_business_success_updated'), 'success');
      }

      setIsFormOpen(false);
      await reloadBusinessLines();
    } catch (error) {
      console.error('Error saving business line:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_business_error_save');

      showSnackbar(errorMessage, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteBusinessLine(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);

      showSnackbar(t('config_business_success_deleted'), 'success');

      await reloadBusinessLines();
    } catch (error) {
      console.error('Error deleting business line:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_business_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Button || !Input || !Table || !Badge || !IconButton || !Modal)
    return null;

  return (
    <div>
      <div className='row mb-3'>
        <div className='col-5'>
          <Input
            icon='search'
            name='searchBusiness'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_business_search_placeholder')}
            type='text'
            value={search}
          />
        </div>
        <div className='col-4'>
          <Dropdown
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(option) => {
              setStatusFilterValue(option?.value || FILTER_VALUES.all);
              setPage(0);
            }}
            placeholder={t('config_business_filter_all')}
            width='100%'
            isDisabled={loading}
          />
        </div>
        <div className='col-3'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_business_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
            />
          </div>
        </div>
      </div>

      <div className='table-container'>
        <Table
          columns={[
            { accessor: 'name', label: t('config_business_col_name'), sort: true },
            {
              accessor: 'status',
              label: t('config_business_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    normalizeStatus(row.status) === 'active'
                      ? t('config_business_status_active')
                      : t('config_business_status_inactive')
                  }
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              label: t('config_business_col_actions'),
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('config_business_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_business_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(0);
          }}
          paginationMode='server'
          totalItems={total}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('config_business_no_results')}</p>
          </div>
        )}
      </div>

      <BusinessCreateEditModal
        Modal={Modal}
        Input={Input}
        show={isFormOpen}
        mode={formMode}
        value={formValue}
        Dropdown={Dropdown}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
        onRequestDelete={
          formMode === 'edit'
            ? () => {
                setIsFormOpen(false);
                setToDelete(formValue);
                setIsDeleteOpen(true);
              }
            : undefined
        }
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_business_confirm_delete_title')}
        message={
          toDelete
            ? t('config_business_confirm_delete_message', {
                name: toDelete.name,
              })
            : t('config_business_confirm_delete_fallback')
        }
        confirmLabel={t('config_business_confirm_delete_button')}
        cancelLabel={t('config_business_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
}
